import { z } from 'zod';

import { env } from '@/env';

const pdfParsedContentSchema = z.object({
  title: z.string().optional(),
  description: z.string().optional(),
  pdfSrc: z.string(),
});

type PdfParsedContent = z.infer<typeof pdfParsedContentSchema>;

export type ParsePdfHtmlContentResult =
  | [PdfParsedContent, undefined]
  | [undefined, z.ZodError<PdfParsedContent>]
  | [undefined, undefined];

const validate = (
  data: Partial<PdfParsedContent>,
): ParsePdfHtmlContentResult => {
  const result = pdfParsedContentSchema.safeParse(data);
  if (result.success) return [result.data, undefined];
  return [undefined, result.error];
};

/**
 * Figure-based format: title from `.figcap`, description from `.figdesc`,
 * and a relative iframe src that gets prefixed with the XP API image URL.
 */
const parseFigurePdf = (doc: Document): ParsePdfHtmlContentResult | null => {
  const figcap = doc.querySelector('.figcap');
  if (!figcap) return null;

  const titleLabel = figcap.querySelector('.fig--title-label');
  const rawTitle = figcap.textContent.trim();
  const title = titleLabel
    ? rawTitle.replace(titleLabel.textContent, '').trim()
    : rawTitle;

  const description = doc.querySelector('.desc.figdesc')?.textContent.trim();

  const pdfSrcPath =
    doc.querySelector('iframe')?.getAttribute('src') ?? undefined;
  if (!pdfSrcPath) {
    return [undefined, undefined];
  }

  const pdfSrc = pdfSrcPath
    ? new URL(pdfSrcPath, env.REACT_APP_XP_API_IMAGE_URL).toString()
    : undefined;

  return validate({ title, description, pdfSrc });
};

/**
 * Embedded CDN format: iframe src is already a full URL, and the
 * description comes from the iframe's `name` attribute.
 */
const parseEmbeddedPdf = (doc: Document): ParsePdfHtmlContentResult | null => {
  const iframe = doc.querySelector('iframe');
  const src = iframe?.getAttribute('src');
  if (!src?.startsWith('http')) return null;

  const description = iframe?.getAttribute('name') ?? undefined;

  return validate({ description, pdfSrc: src });
};

/**
 * Plain iframe format: no figure wrapper or full URL — just a relative
 * iframe src that gets prefixed with the XP API image URL.
 */
const parsePlainIframePdf = (
  doc: Document,
): ParsePdfHtmlContentResult | null => {
  const iframe = doc.querySelector('iframe');
  const pdfSrcPath = iframe?.getAttribute('src');
  if (!pdfSrcPath) return null;

  const pdfSrc = new URL(pdfSrcPath, env.REACT_APP_XP_API_IMAGE_URL).toString();

  return validate({ pdfSrc });
};

/**
 * Attempts each parser strategy in order and returns the first
 * successful result, or the last validation error if all fail.
 */
export const parsePdfHtmlContent = (
  htmlContent: string,
): ParsePdfHtmlContentResult => {
  const doc = new DOMParser().parseFromString(htmlContent, 'text/html');

  const attempts = [parseFigurePdf, parseEmbeddedPdf, parsePlainIframePdf];
  let lastError: ParsePdfHtmlContentResult | null = null;

  for (const attempt of attempts) {
    const result = attempt(doc);
    if (!result) continue;
    if (result[0]) return result;
    if (!result[1]) return result;
    lastError = result;
  }

  return lastError ?? validate({});
};
